/**
 * Analytics API: Mixpanel for events (client); PostHog for feature flags (server + optional client).
 * Events are sent directly to Mixpanel from the client.
 */

import { Platform } from 'react-native'
import { logger } from '../helpers/logger.helper'
import type { AnalyticsEventProperties, AnalyticsScreenName } from './events'
import { getPostHogClientWeb } from './client.web'
import { initMixpanelWeb, mixpanelTrack, mixpanelIdentify, mixpanelReset } from './mixpanel.client'
import { getAnalyticsContext } from './analyticsContext.helper'

export { ANALYTICS_EVENTS, ANALYTICS_SCREENS } from './events'
export type { AnalyticsEventName, AnalyticsEventProperties, AnalyticsScreenName } from './events'
export { useAnalytics } from './useAnalytics'
export type { UseAnalyticsReturn } from './useAnalytics'

/** Init Mixpanel for web. Call once from AnalyticsProvider. No-op on native (native uses Mixpanel SDK in provider). */
export function initMixpanel(token: string): void {
  if (Platform.OS === 'web') {
    logger.debug('[Analytics] initMixpanel called', { tokenPresent: !!token })
    initMixpanelWeb(token)
  }
}

/**
 * Track a custom event. Use ANALYTICS_EVENTS. Sends to Mixpanel only.
 * On native prefer useAnalytics().track() from a component.
 */
export function track(event: string, properties?: AnalyticsEventProperties): void {
  if (Platform.OS === 'web') {
    const props = { ...getAnalyticsContext(), ...properties }
    mixpanelTrack(event, props)
    return
  }
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.debug('[analytics] track (native): use useAnalytics() hook', event, properties)
  }
}

/**
 * Identify user. Sends to Mixpanel only.
 * On native use useAnalytics().identify() from layout.
 */
export function identify(distinctId: string, properties?: Record<string, unknown>): void {
  if (Platform.OS === 'web') {
    const props = { ...getAnalyticsContext(), ...properties }
    mixpanelIdentify(distinctId, props)
    return
  }
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.debug('[analytics] identify (native): use useAnalytics().identify() in layout', distinctId)
  }
}

/**
 * Record screen view. Sends to Mixpanel only.
 * On native use useAnalytics().screen().
 */
export function screen(screenName: AnalyticsScreenName | string, properties?: AnalyticsEventProperties): void {
  if (Platform.OS === 'web') {
    const props = { ...getAnalyticsContext(), screen: screenName, ...properties }
    mixpanelTrack('$pageview', { $current_url: screenName, ...props })
    return
  }
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.debug('[analytics] screen (native): use useAnalytics().screen()', screenName)
  }
}

/** Reset analytics state (e.g. on sign out). Mixpanel only. */
export function reset(): void {
  if (Platform.OS === 'web') {
    mixpanelReset()
  }
}

/** PostHog client for web (feature flags / optional). Do not use for event tracking. */
export { getPostHogClientWeb }
